import {
  ColumnsPanelTrigger,
  GridColDef,
  GridSingleSelectColDef,
  gridColumnLookupSelector,
  GridCsvExportOptions,
  gridFilterActiveItemsSelector,
  GridFilterItem,
  GridRowSelectionModel,
  GridToolbarProps,
  QuickFilter,
  ToolbarButton,
  useGridSelector,
  Toolbar,
  ExportCsv,
  FilterPanelTrigger,
  GridFilterListIcon,
  QuickFilterControl,
  QuickFilterClear,
} from "@mui/x-data-grid";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import {
  capitalize,
  Chip,
  InputAdornment,
  Stack,
  styled,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Cancel,
  Download,
  Search,
  ViewColumn,
} from "@mui/icons-material";
import React from "react";
import { EscalationTableFields, langauge } from "shared";

import { toolbarButtonSx } from "../../../lib/datagrid";
import { InvoiceTableFields } from "../../../hooks/useInvoices";

import { ExportAllModal } from "./ExportAllModal";

type Contexts = `invoices` | `actions` | `contacts` | `escalations` | `quotes`;

type CustomGenericToolbarProps = GridToolbarProps & {
  onRemoveFilter: (filterId: GridFilterItem[`id`]) => void;
  rowSelectionModel: GridRowSelectionModel;
  children?: React.ReactNode;
  csvOptions?: GridCsvExportOptions;
  presetFilters?: React.ReactNode;
  context: Contexts;
  leftActions?: React.ReactNode;
  /** When provided, the export button opens the "email me all pages" export instead of the client-side CSV download */
  onExportAll?: (filename: string | undefined, columns: string[]) => void | Promise<void>;
};

// Persistent quick-search field (design: search box always visible, left of
// the labeled toolbar buttons). Fixed width on purpose: growing it on focus
// re-wrapped the toolbar, so the row changed height every time the box was
// focused or blurred.
const StyledSearchField = styled(TextField)(({ theme }) => ({
  width: 320,
  maxWidth: `100%`,
  '& .MuiOutlinedInput-root': {
    borderRadius: `6px`,
    fontSize: `13px`,
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiOutlinedInput-input': {
    paddingTop: `7px`,
    paddingBottom: `7px`,
  },
}));

// Resolve a singleSelect filter value (often an id) to its display label using
// the column's own valueOptions — so active-filter chips show the name, not the
// raw id (the dropdown already does this; the chips previously didn't).
function getSingleSelectLabel(column: GridColDef | undefined, rawValue: unknown): string | null {
  if (!column || column.type !== `singleSelect` || rawValue === undefined || rawValue === null) {
    return null;
  }

  const singleSelect = column as GridSingleSelectColDef;

  let options;

  try {
    options = typeof singleSelect.valueOptions === `function`
      ? singleSelect.valueOptions({ field: singleSelect.field })
      : singleSelect.valueOptions;
  }
  catch {
    return null;
  }

  if (!Array.isArray(options)) {
    return null;
  }

  const match = options.find(option => {
    const optionValue = singleSelect.getOptionValue
      ? singleSelect.getOptionValue(option)
      : (option && typeof option === `object` ? (option as { value: unknown }).value : option);

    return String(optionValue) === String(rawValue);
  });

  if (match === undefined) {
    return null;
  }

  const label = singleSelect.getOptionLabel
    ? singleSelect.getOptionLabel(match)
    : (match && typeof match === `object` ? (match as { label: unknown }).label : match);

  return label === undefined || label === null ? null : String(label);
}

export function CustomGenericToolbar({
  rowSelectionModel,
  onRemoveFilter,
  children,
  presetFilters,
  csvOptions,
  context,
  leftActions,
  onExportAll,
}: CustomGenericToolbarProps) {
  const apiRef = useGridApiContext();
  const [exportAllOpen, setExportAllOpen] = React.useState(false);
  const activeFilters = useGridSelector(apiRef, gridFilterActiveItemsSelector);
  const columns = useGridSelector(apiRef, gridColumnLookupSelector);
  const exportText = rowSelectionModel.ids.size > 0 ? `Export ${rowSelectionModel.ids.size} row${rowSelectionModel.ids.size === 1 ? `` : `s`}` : `Export`;

  function getSearchPlaceholder() {
    switch (context) {
    case `invoices`:
      return `Quickly search number, contact or reference...`;
    case `actions`:
      return `Quickly search contact or invoice...`;
    case `contacts`:
      return `Quickly search name, email or account number...`;
    case `escalations`:
      return `Quickly search contact or invoice...`;
    case `quotes`:
      return `Quickly search number or contact...`;
    default:
      return `Quickly search...`;
    }
  }

  function getDateFields() {
    switch (context) {
    case `invoices`:
      return [
        InvoiceTableFields.invoiceDueDate,
        InvoiceTableFields.invoiceIssueDate,
      ];
    case `actions`:
      return [
        `actionOn`,
        `executedOn`,
      ];
    case `contacts`:
      return [];
    case `escalations`:
      return [
        EscalationTableFields.ScheduledDate,
      ];
    default:
      return [];
    }
  }

  const showFilterRow = activeFilters.length > 0 || Boolean(presetFilters);

  return (
    // Neutralise the grid toolbar's default flex/padding so the two rows below
    // control their own spacing (design: 10px 12px per row). flex/minHeight
    // must stay content-sized — the default is `flex: 0 1 1px`, so without
    // them the toolbar collapses to ~1px and the rows overflow the grid.
    <Toolbar style={ { display: `block`, padding: 0, flex: `0 0 auto`, minHeight: `fit-content`, height: `fit-content`, borderBottom: `none` } }>
      <div>
        <div className={ `flex flex-row flex-wrap justify-between items-center gap-x-3 gap-y-2 border-b border-gray-100 px-3 py-2` }>
          <div className={ `flex flex-wrap items-center gap-1` }>
            <QuickFilter
              parser={ value => [value] }
              debounceMs={ 350 }
              expanded
            >
              <QuickFilterControl
                render={ ({ ref, ...controlProps }, state) => (
                  <StyledSearchField
                    { ...controlProps }
                    inputRef={ ref }
                    aria-label={ `Search` }
                    placeholder={ getSearchPlaceholder() }
                    size={ `small` }
                    slotProps={ {
                      input: {
                        startAdornment: (
                          <InputAdornment position={ `start` }>
                            <Search fontSize={ `small` } />
                          </InputAdornment>
                        ),
                        endAdornment: state.value ? (
                          <InputAdornment position={ `end` }>
                            <QuickFilterClear
                              edge={ `end` }
                              size={ `small` }
                              aria-label={ `Clear search` }
                              material={ { sx: { marginRight: -0.75 } } }
                            >
                              <Cancel fontSize={ `small` } />
                            </QuickFilterClear>
                          </InputAdornment>
                        ) : null,
                        ...controlProps.slotProps?.input,
                      },
                      ...controlProps.slotProps,
                    } }
                  />
                ) }
              />
            </QuickFilter>

            <FilterPanelTrigger render={ <ToolbarButton material={ { sx: toolbarButtonSx } } /> }>
              <GridFilterListIcon fontSize={ `small` } />
              { `Filters` }
              { activeFilters.length > 0 && (
                <span className={ `rounded-full bg-paidniceGreen-600 px-1.5 text-[11px] font-semibold text-white` }>
                  { activeFilters.length }
                </span>
              ) }
            </FilterPanelTrigger>

            <ColumnsPanelTrigger render={ <ToolbarButton material={ { sx: toolbarButtonSx } } /> }>
              <ViewColumn fontSize={ `small` } />
              { `Columns` }
            </ColumnsPanelTrigger>

            { onExportAll ? (
              <>
                <Tooltip title={ `Export all (emailed)` }>
                  <ToolbarButton
                    material={ { sx: toolbarButtonSx } }
                    onClick={ () => setExportAllOpen(true) }
                  >
                    <Download fontSize={ `small` }/>
                    { `Export` }
                  </ToolbarButton>
                </Tooltip>

                <ExportAllModal
                  open={ exportAllOpen }
                  setOpen={ setExportAllOpen }
                  entityLabel={ context }
                  onConfirm={ onExportAll }
                />
              </>
            ) : (
              <Tooltip title={ exportText }>
                <ExportCsv
                  render={ <ToolbarButton material={ { sx: toolbarButtonSx } } /> }
                  options={ csvOptions }
                >
                  <Download fontSize={ `small` }/>
                  { `Export` }
                </ExportCsv>
              </Tooltip>
            ) }

            { leftActions ? <div className={ `mx-1 h-[18px] w-px bg-gray-200` } /> : null }
            { leftActions }
          </div>

          { children }
        </div>

        { showFilterRow && (
          <div className={ `flex flex-wrap items-center gap-2 border-b border-gray-100 px-3 py-2` }>
            <Stack direction={ `row` }
              sx={ { gap: 0.5, flex: 1, alignItems: `center`, flexWrap: `wrap` } }>
              { activeFilters.length > 0 && (
                <span className={ `text-xs font-medium text-gray-500 mr-1` }>{ `Filters:` }</span>
              ) }
              { activeFilters.map(filter => {
                const column = columns[filter.field];
                const field = column?.headerName || filter.field;
                // Custom operators carry their own label, getLocaleText throws for keys MUI doesn't know
                const operator = column?.filterOperators?.find(op => op.value === filter.operator)?.label ??
                  apiRef.current.getLocaleText(
                    `filterOperator${capitalize(filter.operator)}` as `filterOperatorContains`,
                  );

                const isDate = getDateFields().includes(filter.field);
                const optionLabel = isDate ? null : getSingleSelectLabel(column, filter.value);

                let value = isDate
                  ? new Date(filter.value).toLocaleDateString()
                  : (optionLabel ?? filter.value ?? ``);

                // Fallbacks only when the column's valueOptions didn't resolve a label
                if (!isDate && optionLabel === null) {
                  if (context === `actions` && filter.field === `type`) {
                    value = langauge.action[value]?.label || value;
                  }
                  else if (context === `escalations` && filter.field === EscalationTableFields.Status) {
                    value = langauge.escalation.status[value];
                  }
                }

                return (
                  <Chip
                    key={ filter.id }
                    label={ (
                      <span>
                        { `${field} ${operator} ` }
                        <strong className={ `font-semibold` }>{ `${value}` }</strong>
                      </span>
                    ) }
                    onDelete={ () => onRemoveFilter(filter.id) }
                    onClick={ () => onRemoveFilter(filter.id) }
                    sx={ {
                      mx: 0.25,
                      backgroundColor: `#f1f6f8`, // paidniceGreen-50
                      border: `1px solid #b3cddb`, // paidniceGreen-200
                      color: `#2b495a`, // paidniceGreen-700
                      fontWeight: 500,
                      '& .MuiChip-deleteIcon': {
                        color: `#467691`, // paidniceGreen-500
                      },
                      '& .MuiChip-deleteIcon:hover': {
                        color: `#2b495a`,
                      },
                    } }
                    size={ `small` }
                  />
                );
              }) }
              { activeFilters.length > 0 && (
                <button
                  type={ `button` }
                  className={ `ml-1 text-xs text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline` }
                  onClick={ () => apiRef.current.setFilterModel({ items: [] }) }
                >
                  { `Clear all` }
                </button>
              ) }
            </Stack>

            { presetFilters }
          </div>
        ) }
      </div>
    </Toolbar>
  );
}
